import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Check, MessageCircle, Monitor, PhoneCall } from 'lucide-react';
import backendApi from '../../services/backendApi';
import PomodoroControl from '../layout/PomodoroControl';
import TeamChatPanel from './TeamChatPanel';
import { announceIntercomCall, announceNewTicket, announcePomodoroEnd, announcePomodoroStart, announceUpcomingEvent } from '../../utils/pomodoroAnnouncements';
import { isIntercomTvDashboard, setIntercomTvDashboard, subscribeToIntercomTvMode } from '../../utils/intercomDevice';
import { useVoiceCall } from '../../context/VoiceCallContext';
import './teamTvDashboard.css';

const SAMPLE_DATA = {
  generated_at: '2026-09-05T14:00:00+01:00',
  team: [
    { id: 1, name: 'Sofia', department_id: 4, department_name: 'Développement', status: 'focus', status_ends_at: '2026-09-05T15:25:00+01:00', pomodoro_total_seconds: 1500, pomodoro_remaining_seconds: 1122, activity: { treatedUS: 5, plannedUS: 4, treatedSP: 18, plannedSP: 15, commitmentPct: 120, onTimeUS: 4, scheduledTreatedUS: 5, onTimePct: 80, resolvedTickets: 2, unplannedUS: 1 } },
    { id: 2, name: 'Yassine', department_id: 4, department_name: 'Développement', status: 'meeting', status_ends_at: '2026-09-05T15:30:00+01:00', activity: { treatedUS: 3, plannedUS: 5, treatedSP: 8, plannedSP: 13, commitmentPct: 62, onTimeUS: 2, scheduledTreatedUS: 3, onTimePct: 67, resolvedTickets: 1, unplannedUS: 0 } },
    { id: 3, name: 'Lina', department_id: 4, department_name: 'Développement', status: 'available', status_ends_at: null, activity: { treatedUS: 4, plannedUS: 4, treatedSP: 11, plannedSP: 11, commitmentPct: 100, onTimeUS: 4, scheduledTreatedUS: 4, onTimePct: 100, resolvedTickets: 3, unplannedUS: 1 } },
    { id: 4, name: 'Mehdi', department_id: 7, department_name: 'Gestion de projet', status: 'leave', status_ends_at: null, poActivity: { testedUS: 7, doneUS: 5, donePct: 71, validatedWithinTwoDaysUS: 4, validationWithinTwoDaysPct: 57, sprintIntegrationUS: 3, backlogEligibleUS: 4, sprintIntegrationPct: 75, pendingTestUS: 3, pendingOverTwoDaysUS: 1, averagePendingTestDays: 2.3 } },
    { id: 5, name: 'Amine', department_id: 7, department_name: 'Gestion de projet', status: 'focus', status_ends_at: '2026-09-05T15:14:00+01:00', pomodoro_total_seconds: 1500, pomodoro_remaining_seconds: 435, poActivity: { testedUS: 4, doneUS: 3, donePct: 75, validatedWithinTwoDaysUS: 3, validationWithinTwoDaysPct: 75, sprintIntegrationUS: 2, backlogEligibleUS: 2, sprintIntegrationPct: 100, pendingTestUS: 1, pendingOverTwoDaysUS: 0, averagePendingTestDays: 1 } },
    { id: 6, name: 'Nora', department_id: 7, department_name: 'Gestion de projet', status: 'mission', status_ends_at: '2026-09-05T17:00:00+01:00', poActivity: { testedUS: 6, doneUS: 2, donePct: 33, validatedWithinTwoDaysUS: 2, validationWithinTwoDaysPct: 33, sprintIntegrationUS: 1, backlogEligibleUS: 3, sprintIntegrationPct: 33, pendingTestUS: 2, pendingOverTwoDaysUS: 2, averagePendingTestDays: 4.5 } },
  ],
  activity: { treatedUS: 12, plannedUS: 13, treatedSP: 37, plannedSP: 39, commitmentPct: 95, onTimeUS: 10, scheduledTreatedUS: 12, onTimePct: 83, averageAllocatedHours: 6.3 },
  poSummary: { pendingTestUS: 6, pendingOverTwoDaysUS: 3, averagePendingTestDays: 2.9 },
  agenda: [
    { id: 101, starts_at: '2026-09-05T09:30:00+01:00', ends_at: '2026-09-05T10:00:00+01:00', title: 'Point quotidien', context: 'Équipe', organizer_initials: 'SO', organizer_color: '#7aa2f7', participant_initials: ['SO', 'YA', 'LI', 'ME'] },
    { id: 102, starts_at: '2026-09-05T11:00:00+01:00', ends_at: '2026-09-05T12:00:00+01:00', title: 'Revue WMS', context: 'Logistique & stock', project_code: 'WMS', organizer_initials: 'YA', organizer_color: '#bb9af7', participant_initials: ['YA', 'AM'] },
    { id: 103, starts_at: '2026-09-05T14:30:00+01:00', ends_at: '2026-09-05T15:15:00+01:00', title: 'Atelier client', context: 'Atlas Distribution', project_code: 'ATL', organizer_initials: 'NO', organizer_color: '#e0af68', participant_initials: ['NO', 'SO'] },
    { id: 104, starts_at: '2026-09-05T16:00:00+01:00', ends_at: '2026-09-05T16:45:00+01:00', title: 'Affinage du sprint', context: 'Sprint 24', project_code: 'ECO', organizer_initials: 'LI', organizer_color: '#7dcfff', participant_initials: ['LI', 'ME', 'AM'] },
  ],
  sprint: {
    name: 'Sprint 24', total_points: 134, completed_points: 76, remaining_points: 58, overdue_count: 4,
    projects: [
      { id: 201, code: 'ECO', name: 'E-commerce B2B', progress_percent: 70, tone: 'good' },
      { id: 202, code: 'WMS', name: 'Logistique & stock', progress_percent: 50, tone: 'watch' },
      { id: 203, code: 'FIN', name: 'Finance Maroc', progress_percent: 50, tone: 'good' },
    ],
  },
  support: {
    open_count: 12, new_count: 3, urgent_count: 2,
    received_by_day: [
      { date: '2026-09-01', count: 2 }, { date: '2026-09-02', count: 4 },
      { date: '2026-09-03', count: 1 }, { date: '2026-09-04', count: 3 },
      { date: '2026-09-05', count: 5 }, { date: '2026-09-06', count: 0 },
      { date: '2026-09-07', count: 2 }, { date: '2026-09-08', count: 3 },
      { date: '2026-09-09', count: 1 }, { date: '2026-09-10', count: 4 },
      { date: '2026-09-11', count: 0 }, { date: '2026-09-12', count: 0 },
      { date: '2026-09-13', count: 0 }, { date: '2026-09-14', count: 0 },
    ],
    clients: [
      { partner_id: 301, name: 'Atlas Distribution', open_count: 5, urgent_count: 1 },
      { partner_id: 302, name: 'Nova Retail', open_count: 4, urgent_count: 1 },
      { partner_id: 303, name: 'Mediservice', open_count: 3, urgent_count: 0 },
    ],
  },
  milestones: [
    { id: 401, due_on: '2026-09-08', title: 'Recette du portail B2B', project_code: 'ECO', owner_initials: 'SO', owner_color: '#7aa2f7', is_overdue: false },
    { id: 402, due_on: '2026-09-10', title: 'Validation des flux stock', project_code: 'WMS', owner_initials: 'YA', owner_color: '#bb9af7', is_overdue: false },
    { id: 403, due_on: '2026-09-12', title: 'Clôture comptable pilote', project_code: 'FIN', owner_initials: 'NO', owner_color: '#e0af68', is_overdue: false },
    { id: 404, due_on: '2026-09-03', title: 'Import des tarifs clients', project_code: 'ECO', owner_initials: 'LI', owner_color: '#7dcfff', is_overdue: true },
  ],
};

const formatClock = (value) => new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
const formatCountdown = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
const monthLabel = (value) => new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(new Date(value));
const formatNumber = value => value == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(value);
const formatPct = value => value == null ? '—' : `${formatNumber(value)} %`;
const readableSprintName = value => {
  const label = String(value || '').trim();
  return /[A-Za-zÀ-ÿ0-9]/.test(label) ? label : '';
};

const formatPeriodLabel = (period, sprintName) => {
  if (!period?.startDate) return 'Période complète';
  const start = new Date(`${period.startDate}T00:00:00Z`);
  const end = new Date(`${period.endDate}T00:00:00Z`);
  if (period.period === 'sprint') {
    const dates = `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;
    const label = readableSprintName(sprintName) || readableSprintName(period.label);
    return label ? `${label} · ${dates}` : dates;
  }
  if (period.period === 'day') return start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  if (period.period === 'month') return start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;
};

const relativeDeadline = (value, overdue) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${value}T00:00:00`);
  const days = Math.round((due - today) / 86400000);
  if (overdue || days < 0) return `En retard de ${Math.abs(days)} j`;
  if (days === 0) return "Aujourd'hui";
  return `Dans ${days} jours`;
};

const STATUS_TEXT = { focus: 'Concentration', meeting: 'Réunion', available: 'Disponible', leave: 'En congé', mission: 'En mission' };

const departmentTone = value => {
  const seed = String(value ?? 'unassigned');
  const hash = [...seed].reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 0);
  return hash % 4;
};

const groupTeamByDepartment = team => {
  const groups = new Map();
  (team || []).forEach(member => {
    const key = member.department_id ?? `name:${member.department_name || 'Sans département'}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: member.department_name || 'Sans département',
        tone: departmentTone(key),
        members: [],
      });
    }
    groups.get(key).members.push(member);
  });
  return [...groups.values()];
};

const isDeveloperDepartment = name => {
  const normalized = String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return normalized === 'dev' || normalized === 'developpement';
};

const isProductOwnerDepartment = name => {
  const normalized = String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return normalized === 'po' || normalized.includes('product owner') || normalized === 'gestion de projet';
};

const userInitials = name => String(name || '?')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part[0]?.toUpperCase())
  .join('') || '?';

const ACTIVITY_RING_WIDTH = 12;
const PO_OUTER_ACTIVITY_RING_RADIUS = 81;
const PO_INNER_ACTIVITY_RING_RADIUS = 65;
const OUTER_ACTIVITY_RING_RADIUS = 67;
const INNER_ACTIVITY_RING_RADIUS = 53;

function ActivityRingCircle({ radius, progress, className }) {
  const normalized = Math.max(0, Math.min(100, Number(progress) || 0));
  return (
    <circle
      className={className}
      cx="66"
      cy="66"
      r={radius}
      pathLength="100"
      strokeDasharray="100"
      strokeDashoffset={100 - normalized}
      strokeWidth={ACTIVITY_RING_WIDTH}
      transform="rotate(-90 66 66)"
    />
  );
}

function Person({ member, onIntercom, onVoiceCall, onChat, callFeedback, isRinging, unreadMessage, canContact }) {
  const [remaining, setRemaining] = useState(member.pomodoro_remaining_seconds || 0);
  const [meetingNow, setMeetingNow] = useState(() => Date.now());
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => setPhotoFailed(false), [member.photo_url]);

  useEffect(() => {
    setRemaining(member.pomodoro_remaining_seconds || 0);
  }, [member.pomodoro_remaining_seconds]);

  useEffect(() => {
    if (member.status !== 'focus' || member.pomodoro_paused) return undefined;
    const timer = window.setInterval(() => setRemaining(value => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [member.status, member.pomodoro_paused]);

  useEffect(() => {
    if (member.status !== 'meeting' || !member.status_ends_at) return undefined;
    const updateClock = () => setMeetingNow(Date.now());
    updateClock();
    const timer = window.setInterval(updateClock, 1000);
    return () => window.clearInterval(timer);
  }, [member.status, member.status_ends_at]);

  const visualStatus = member.status === 'focus' && remaining === 0 ? 'available' : member.status;
  const meetingEndTimestamp = member.status_ends_at ? new Date(member.status_ends_at).getTime() : null;
  const meetingRemaining = Number.isFinite(meetingEndTimestamp)
    ? Math.max(0, Math.ceil((meetingEndTimestamp - meetingNow) / 1000))
    : null;
  const countdown = visualStatus === 'meeting' ? meetingRemaining : remaining;
  const hasCountdown = (visualStatus === 'focus' || visualStatus === 'meeting') && countdown != null;
  const activity = member.activity || {};
  // Le département reste la source de vérité si des métriques d'un autre profil
  // sont remontées par erreur pour un collaborateur.
  const poActivity = isProductOwnerDepartment(member.department_name) ? member.poActivity : null;
  const activityLabel = poActivity
    ? `${poActivity.doneUS || 0} US passées à Done sur ${poActivity.testedUS || 0} mises en Test, ${formatPct(poActivity.donePct)}. ${poActivity.validatedWithinTwoDaysUS || 0} US validées en moins de deux jours, ${formatPct(poActivity.validationWithinTwoDaysPct)}. ${poActivity.sprintIntegrationUS || 0} US intégrées au sprint sur ${poActivity.backlogEligibleUS || 0} entrées au backlog la semaine précédente, ${formatPct(poActivity.sprintIntegrationPct)}.`
    : `${formatNumber(activity.treatedSP || 0)} Story Points traités sur ${formatNumber(activity.plannedSP || 0)} planifiés, ${formatPct(activity.commitmentPct)} d’engagement, ${formatPct(activity.onTimePct)} de respect du planning, ${activity.resolvedTickets || 0} tickets résolus et ${activity.unplannedUS || 0} US hors plan`;
  const outerRingProgress = activity.commitmentPct;
  const innerRingProgress = poActivity ? poActivity.validationWithinTwoDaysPct : activity.onTimePct;
  const backlogSprintProgress = poActivity?.sprintIntegrationPct;
  const hasBacklogSprintIndicator = Boolean(poActivity && Number(poActivity.backlogEligibleUS) > 0);
  const innerRingRadius = poActivity ? PO_INNER_ACTIVITY_RING_RADIUS : INNER_ACTIVITY_RING_RADIUS;
  const detail = visualStatus === 'available' && member.status === 'focus'
    ? STATUS_TEXT.available
    : member.pomodoro_paused
    ? 'Concentration en pause'
    : visualStatus === 'available' || visualStatus === 'leave'
    ? STATUS_TEXT[visualStatus]
    : `${STATUS_TEXT[visualStatus]} · fin ${formatClock(member.status_ends_at)}`;
  const connectionLabel = member.online ? 'Connecté' : 'Hors ligne';
  const actionPending = callFeedback?.status === 'sending';
  const actionAriaLabel = mode => {
    const base = `${mode === 'intercom' ? 'Interphone' : 'Appel vocal'} ${member.name}`;
    if (callFeedback?.mode !== mode) return base;
    const state = callFeedback.status === 'sending' ? 'envoi en cours' : callFeedback.status === 'sent' ? 'envoyé' : 'échec';
    return `${base}, ${state}`;
  };
  const label = `${member.name}, ${connectionLabel}, ${detail}${hasCountdown ? `, ${formatCountdown(countdown)} restantes` : ''}. ${activityLabel}`;

  return (
    <article className={`tv-person ${poActivity ? 'tv-person--po' : ''} tv-person--${visualStatus} ${isRinging ? 'is-ringing' : ''}`} aria-label={label}>
      <div className="tv-person-portrait">
        <div className="tv-portrait-ring" title={activityLabel}>
          <svg className="tv-activity-rings" viewBox="0 0 132 132" aria-hidden="true">
            {hasBacklogSprintIndicator && <circle className="tv-activity-track" cx="66" cy="66" r={PO_OUTER_ACTIVITY_RING_RADIUS} strokeWidth={ACTIVITY_RING_WIDTH} />}
            {!poActivity && <circle className="tv-activity-track" cx="66" cy="66" r={OUTER_ACTIVITY_RING_RADIUS} strokeWidth={ACTIVITY_RING_WIDTH} />}
            <circle className="tv-activity-track" cx="66" cy="66" r={innerRingRadius} strokeWidth={ACTIVITY_RING_WIDTH} />
            {hasBacklogSprintIndicator && <ActivityRingCircle radius={PO_OUTER_ACTIVITY_RING_RADIUS} progress={backlogSprintProgress} className="tv-activity-progress tv-activity-progress--po-backlog-sprint" />}
            {!poActivity && <ActivityRingCircle radius={OUTER_ACTIVITY_RING_RADIUS} progress={outerRingProgress} className="tv-activity-progress tv-activity-progress--commitment" />}
            <ActivityRingCircle radius={innerRingRadius} progress={innerRingProgress} className={`tv-activity-progress ${poActivity ? 'tv-activity-progress--po-speed' : 'tv-activity-progress--ontime'}`} />
          </svg>
          <div className="tv-portrait-frame">
            {member.photo_url && !photoFailed
              ? <img src={member.photo_url} alt="" onError={() => setPhotoFailed(true)} />
              : <span className="tv-portrait-initials" aria-hidden="true">{userInitials(member.name)}</span>}
          </div>
          {canContact && <div className="tv-contact-actions" aria-label={`Contacter ${member.name}`}>
            <button
              type="button"
              className="tv-contact-action is-chat"
              aria-label={`Écrire à ${member.name}`}
              title={`Écrire à ${member.name}`}
              onClick={() => onChat(member)}
            >
              <MessageCircle />
            </button>
            <button
              type="button"
              className="tv-contact-action is-intercom"
              aria-label={actionAriaLabel('intercom')}
              title={`Interphone ${member.name}`}
              aria-busy={actionPending && callFeedback?.mode === 'intercom'}
              disabled={actionPending}
              onClick={() => onIntercom(member)}
            >
              {callFeedback?.mode === 'intercom' && callFeedback.status === 'sent' ? <Check /> : <Bell />}
            </button>
            <button
              type="button"
              className="tv-contact-action is-voice"
              aria-label={actionAriaLabel('voice')}
              title={`Appel vocal ${member.name}`}
              aria-busy={actionPending && callFeedback?.mode === 'voice'}
              disabled={actionPending}
              onClick={() => onVoiceCall(member)}
            >
              {callFeedback?.mode === 'voice' && callFeedback.status === 'sent' ? <Check /> : <PhoneCall />}
            </button>
          </div>}
          {unreadMessage && (
            <button
              type="button"
              className="tv-unread-message"
              aria-label={`Message non lu de ${member.name} : ${unreadMessage.body}`}
              onClick={() => onChat(member)}
            >
              <span>{unreadMessage.body}</span>
              <time dateTime={unreadMessage.created_at}>{formatClock(unreadMessage.created_at)}</time>
            </button>
          )}
          <span
            className={`tv-status-mark ${member.online ? 'is-online' : 'is-offline'}`}
            role="status"
            aria-label={`${member.name} est ${member.online ? 'connecté' : 'hors ligne'}`}
            title={connectionLabel}
          />
          {callFeedback?.status === 'sent' && <span className="tv-call-mark is-visible" aria-hidden="true"><Check /></span>}
          {!poActivity && activity.commitmentPct > 100 && <span className="tv-ring-overflow">{activity.commitmentPct}%</span>}
        </div>
      </div>
      <div className="tv-identity">
        {hasCountdown && <b aria-label={visualStatus === 'meeting' ? 'Temps restant de la réunion' : 'Temps restant du Pomodoro'}>{formatCountdown(countdown)}</b>}
        <span>{detail}</span>
        <small>{poActivity
          ? `${poActivity.doneUS || 0}/${poActivity.testedUS || 0} Done · ${poActivity.validatedWithinTwoDaysUS || 0}/${poActivity.testedUS || 0} sous 2 j · ${poActivity.sprintIntegrationUS || 0}/${poActivity.backlogEligibleUS || 0} sprint`
          : `${activity.treatedUS || 0}/${activity.plannedUS || 0} US · ${activity.resolvedTickets || 0} tickets · ${activity.unplannedUS || 0} hors plan`}
        </small>
      </div>
    </article>
  );
}

function VerticalChart({ items, support = false }) {
  const maximum = Math.max(...items.map(item => support ? item.open_count : 100), 1);
  return (
    <div
      className={`tv-vertical-chart ${support ? 'tv-vertical-chart--support' : ''} ${items.length > 6 ? 'is-dense' : ''}`}
      style={{ gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))` }}
    >
      {items.map(item => {
        const value = support ? item.open_count : item.progress_percent;
        const urgent = support && item.urgent_count > 0;
        return (
          <article className="tv-vertical-item" key={item.id || item.partner_id}>
            <b>{value}{support ? '' : '%'}</b>
            <div className="tv-vertical-plot">
              <div
                className={`tv-vertical-bar ${urgent ? 'is-urgent' : ''} ${item.tone === 'watch' ? 'is-watch' : ''}`}
                role="meter"
                aria-label={support ? `${value} tickets ouverts pour ${item.name}` : `Progression de ${item.name}`}
                aria-valuenow={value}
                aria-valuemin="0"
                aria-valuemax={support ? maximum : 100}
                style={{ height: `${(value / maximum) * 100}%` }}
              />
            </div>
            <h3 title={support ? item.name : undefined}>{support ? item.name : item.code}</h3>
          </article>
        );
      })}
    </div>
  );
}

function DailyTicketsChart({ items = [] }) {
  const maximum = Math.max(...items.map(item => item.count), 1);
  const total = items.reduce((sum, item) => sum + item.count, 0);
  const peak = items.reduce((highest, item) => item.count > (highest?.count || 0) ? item : highest, null);
  const labelForDate = value => new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));

  return (
    <>
      <div className="tv-summary tv-summary--daily">
        <div><b>{total}</b><span>reçus sur le sprint</span></div>
        <div><b>{peak?.count || 0}</b><span>pic journalier</span></div>
      </div>
      {items.length ? (
        <div className="tv-daily-chart" role="img" aria-label={`${total} tickets d’assistance reçus pendant le sprint`}>
          <div className="tv-daily-grid" aria-hidden="true"><i /><i /><i /></div>
          <div className="tv-daily-bars" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(22px, 1fr))` }}>
            {items.map((item, index) => (
              <div className="tv-daily-item" key={item.date} title={`${labelForDate(item.date)} : ${item.count} ticket${item.count > 1 ? 's' : ''}`}>
                <b>{item.count}</b>
                <div className="tv-daily-plot">
                  <span style={{ height: `${(item.count / maximum) * 100}%` }} />
                </div>
                <time dateTime={item.date}>{index % 2 === 0 || items.length <= 8 ? new Date(`${item.date}T00:00:00Z`).getUTCDate() : ''}</time>
              </div>
            ))}
          </div>
        </div>
      ) : <div className="tv-daily-empty" role="status">Aucune période de sprint datée</div>}
    </>
  );
}

function TeamActivitySummary({ activity }) {
  const summary = activity || {};
  const cards = [
    { label: 'Engagement', value: `${formatNumber(summary.treatedSP || 0)} / ${formatNumber(summary.plannedSP || 0)} SP · ${formatPct(summary.commitmentPct)}` },
    { label: 'Respect du planning', value: `${summary.onTimeUS || 0} / ${summary.scheduledTreatedUS || 0} US · ${formatPct(summary.onTimePct)}` },
    { label: 'Temps moyen alloué', value: summary.averageAllocatedHours == null ? '—' : `${formatNumber(summary.averageAllocatedHours)} h` },
  ];
  return (
    <div className="tv-activity-summary" aria-label="Indicateurs de toute l’équipe">
      {cards.map(card => <div key={card.label}><span>{card.label}</span><strong>{card.value}</strong></div>)}
    </div>
  );
}

function TeamPoSummary({ summary }) {
  const indicators = summary || {};
  const cards = [
    { label: 'En attente de validation', value: `${indicators.pendingTestUS || 0} US` },
    { label: 'Depuis plus de 2 jours', value: `${indicators.pendingOverTwoDaysUS || 0} US` },
    { label: 'Temps moyen en Test', value: `${formatNumber(indicators.averagePendingTestDays || 0)} j` },
  ];
  return (
    <div className="tv-activity-summary tv-po-summary" aria-label="Indicateurs des Product Owners">
      {cards.map(card => <div key={card.label}><span>{card.label}</span><strong>{card.value}</strong></div>)}
    </div>
  );
}

function TeamTvContent({ data, isStale = false, onIntercom, onVoiceCall, onChat, unreadBySender, currentUserId, callFeedback, activeCall, isTvMode, onTvModeChange, activityPeriodMeta, activityLoading }) {
  const sprintPercent = useMemo(() => Math.round((data.sprint.completed_points / Math.max(data.sprint.total_points, 1)) * 100), [data]);
  const departmentGroups = useMemo(() => groupTeamByDepartment(data.team), [data.team]);
  const [agendaNow, setAgendaNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setAgendaNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const isExpiredAgendaEvent = event => {
    const endTimestamp = new Date(event.ends_at).getTime();
    return Number.isFinite(endTimestamp) && agendaNow - endTimestamp >= 2 * 60 * 60 * 1000;
  };
  const agendaColumns = data.agenda
    .map(event => isExpiredAgendaEvent(event) ? 'minmax(140px, 180px)' : 'minmax(260px, 460px)')
    .join(' ');

  return (
    <main className="team-tv">
      <PomodoroControl />
      {isStale && <div className="tv-data-state" role="status">Dernières données disponibles</div>}
      {activeCall && (
        <aside className="tv-intercom-alert" role="alert" aria-live="assertive">
          <span className="tv-intercom-symbol" aria-hidden="true"><PhoneCall /></span>
          <div><span>Appel interphone</span><strong>{activeCall.target_name}</strong><small>{activeCall.caller_name} te demande</small></div>
        </aside>
      )}
      <section className="tv-presence tv-zone tv-zone--presence" aria-label="Présence de l’équipe">
        <header className="tv-zone-heading">
          <div><h2>Équipe et disponibilités</h2><p>Présence, appels et Pomodoro</p></div>
          <div className="tv-presence-actions">
            <div className="tv-sprint-period" aria-label="Période des indicateurs">
              <span>Sprint en cours</span>
              <strong>{activityLoading ? 'Actualisation…' : formatPeriodLabel(activityPeriodMeta, data.sprint.name)}</strong>
            </div>
            <button
              type="button"
              className={`tv-mode-toggle ${isTvMode ? 'is-active' : ''}`}
              aria-pressed={isTvMode}
              title={isTvMode ? 'Désactiver les annonces collectives sur ce navigateur' : 'Lire les annonces collectives sur ce navigateur'}
              onClick={() => onTvModeChange(!isTvMode)}
            >
              <Monitor aria-hidden="true" />
              <span>{isTvMode ? 'Mode TV actif' : 'Activer le mode TV'}</span>
            </button>
          </div>
        </header>
        <div className="tv-department-groups">
          {departmentGroups.map((department, index) => (
            <section
              className={`tv-department-group tv-department-group--tone-${department.tone} ${isDeveloperDepartment(department.name) || isProductOwnerDepartment(department.name) ? 'has-activity-summary' : ''}`}
              aria-labelledby={`tv-department-${index}`}
              key={department.key}
            >
              <h3 id={`tv-department-${index}`}>{department.name}</h3>
              {isDeveloperDepartment(department.name) && <TeamActivitySummary activity={data.activity} />}
              {isProductOwnerDepartment(department.name) && <TeamPoSummary summary={data.poSummary} />}
              <div
                className={`tv-team-row ${isProductOwnerDepartment(department.name) ? 'tv-team-row--po' : ''}`}
                style={{ gridTemplateColumns: `repeat(${Math.max(department.members.length, 1)}, minmax(100px, 1fr))` }}
              >
                {department.members.map(member => <Person
                  key={member.id}
                  member={member}
                  onIntercom={onIntercom}
                  onVoiceCall={onVoiceCall}
                  onChat={onChat}
                  unreadMessage={unreadBySender.get(member.id)}
                  canContact={currentUserId == null || member.id !== currentUserId}
                  callFeedback={callFeedback?.targetId === member.id ? callFeedback : null}
                  isRinging={activeCall?.target_id === member.id}
                />)}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section className="tv-agenda tv-zone tv-zone--agenda" aria-label="Agenda de l’équipe">
        <header className="tv-zone-heading"><div><h2>Agenda du jour</h2><p>Réunions et événements de l’équipe</p></div></header>
        {data.agenda.length > 0 ? (
          <div
            className="tv-event-row"
            style={{ gridTemplateColumns: agendaColumns }}
          >
            {data.agenda.map(event => {
              const isExpired = isExpiredAgendaEvent(event);
              return (
                <article className={`tv-event ${isExpired ? 'is-expired' : ''}`} key={event.id} aria-label={isExpired ? `${event.title}, terminé depuis plus de deux heures` : undefined} title={isExpired ? `${event.title} · terminé à ${formatClock(event.ends_at)}` : undefined}>
                  {isExpired ? (
                    <div className="tv-event-expired" aria-hidden="true">
                      <Check />
                      <strong>{event.title}</strong>
                      <time>{formatClock(event.ends_at)}</time>
                      {event.project_code && <b className="tv-event-project tv-event-project--expired">{event.project_code}</b>}
                    </div>
                  ) : <>
                    <div className="tv-event-time"><b style={{ color: event.organizer_color }}>{formatClock(event.starts_at)}</b><span>{formatClock(event.ends_at)}</span></div>
                    <div className="tv-event-copy">
                      <h3>{event.title}</h3>
                      <span>{event.context}</span>
                      {event.project_code && <b className="tv-event-project" aria-label={`Projet ${event.project_code}`}>{event.project_code}</b>}
                    </div>
                    <div className="tv-event-people" aria-label={`${event.participant_initials.length} participants, ${event.organizer_initials} organise`}>
                      {event.participant_initials.map((initials, index) => <i key={`${initials}-${index}`} className={initials === event.organizer_initials ? 'is-organizer' : ''} style={initials === event.organizer_initials ? { backgroundColor: event.organizer_color } : undefined}>{initials}</i>)}
                    </div>
                  </>}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="tv-agenda-empty" role="status">
            <span aria-hidden="true" />
            <strong>Aucun événement prévu aujourd’hui</strong>
          </div>
        )}
      </section>

      <section className="tv-operations">
        <section className="tv-panel tv-sprint tv-zone tv-zone--sprint" aria-label={data.sprint.name}>
          <header className="tv-zone-heading"><div><h2>Sprint actif</h2><p>{data.sprint.name} · progression des projets</p></div></header>
          <div className="tv-summary"><div><b>{sprintPercent}%</b><span>réalisé</span></div><div><b>{data.sprint.remaining_points}</b><span>points restants</span></div><div className="is-alert"><b>{data.sprint.overdue_count}</b><span>en retard</span></div></div>
          <VerticalChart items={data.sprint.projects} />
        </section>
        <section className="tv-panel tv-daily-support tv-zone tv-zone--daily-support" aria-label="Tickets reçus par jour">
          <header className="tv-zone-heading"><div><h2>Tickets reçus</h2><p>Assistance · volume quotidien du sprint</p></div></header>
          <DailyTicketsChart items={data.support.received_by_day || []} />
        </section>
        <section className="tv-panel tv-support tv-zone tv-zone--support" aria-label="Assistance">
          <header className="tv-zone-heading"><div><h2>Assistance</h2><p>Tickets ouverts par client</p></div></header>
          <div className="tv-summary"><div><b>{data.support.open_count}</b><span>ouverts</span></div><div><b>{data.support.new_count}</b><span>nouveaux</span></div><div className="is-alert"><b>{data.support.urgent_count}</b><span>urgents</span></div></div>
          <VerticalChart items={data.support.clients} support />
        </section>
      </section>

      <section className="tv-milestones tv-zone tv-zone--milestones" aria-label="Jalons des projets">
        <header className="tv-zone-heading"><div><h2>Prochains jalons</h2><p>Échéances et responsables</p></div></header>
        <div className="tv-milestone-row">
          {data.milestones.map(milestone => (
            <article className={`tv-milestone ${milestone.is_overdue ? 'is-late' : ''}`} key={milestone.id}>
              <time className="tv-milestone-date"><b style={{ color: milestone.owner_color }}>{new Date(`${milestone.due_on}T00:00:00`).getDate().toString().padStart(2, '0')}</b><span>{monthLabel(`${milestone.due_on}T00:00:00`)}</span></time>
              <div className="tv-milestone-copy"><span className="tv-project-code">{milestone.project_code}</span><h3>{milestone.title}</h3><small>{relativeDeadline(milestone.due_on, milestone.is_overdue)}</small></div>
              <i className="tv-owner" aria-label={`${milestone.owner_initials} est responsable`} style={{ backgroundColor: milestone.owner_color }}>{milestone.owner_initials}</i>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function TeamTvDashboard({ data: providedData, currentUserId = null }) {
  const { startCall } = useVoiceCall();
  const [data, setData] = useState(providedData || null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(!providedData);
  const [isStale, setIsStale] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const previousTeamRef = useRef(null);
  const knownTicketIdsRef = useRef(null);
  const knownIntercomIdsRef = useRef(new Set());
  const intercomAlertTimerRef = useRef(null);
  const [callFeedback, setCallFeedback] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [isTvMode, setIsTvMode] = useState(isIntercomTvDashboard);
  const [chatMember, setChatMember] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState([]);

  const clearUnreadForMember = useCallback(memberId => {
    setUnreadMessages(current => current.filter(message => message.sender_id !== memberId));
  }, []);

  const openChat = useCallback(member => {
    setChatMember(member);
    clearUnreadForMember(member.id);
  }, [clearUnreadForMember]);

  useEffect(() => subscribeToIntercomTvMode(setIsTvMode), []);

  useEffect(() => {
    if (providedData || currentUserId == null) return undefined;
    let mounted = true;
    const loadUnread = async () => {
      try {
        const unread = await backendApi.getUnreadChatMessages();
        if (mounted) setUnreadMessages(unread);
      } catch (error) {
        // Une indisponibilité temporaire du chat ne bloque pas l’écran équipe.
      }
    };
    loadUnread();
    const timer = window.setInterval(loadUnread, 4000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [currentUserId, providedData]);

  useEffect(() => {
    if (currentUserId == null) return undefined;
    const receiveMessage = event => {
      const message = event.detail;
      if (!message || message.recipient_id !== currentUserId || message.sender_id === chatMember?.id) return;
      setUnreadMessages(current => [
        ...current.filter(item => item.sender_id !== message.sender_id),
        message,
      ]);
    };
    window.addEventListener('team-chat:message', receiveMessage);
    return () => window.removeEventListener('team-chat:message', receiveMessage);
  }, [chatMember?.id, currentUserId]);

  useEffect(() => {
    if (!isTvMode) setActiveCall(null);
  }, [isTvMode]);

  const handleTvModeChange = useCallback(enabled => {
    setIsTvMode(setIntercomTvDashboard(enabled));
  }, []);

  const showIntercomCall = useCallback(call => {
    setActiveCall(call);
    announceIntercomCall(call);
    window.clearTimeout(intercomAlertTimerRef.current);
    intercomAlertTimerRef.current = window.setTimeout(() => setActiveCall(null), 12000);
  }, []);

  const showCallFeedback = useCallback((targetId, mode, status) => {
    setCallFeedback({ targetId, mode, status });
    if (status !== 'sending') {
      window.setTimeout(() => setCallFeedback(current => current?.targetId === targetId && current?.mode === mode ? null : current), 3000);
    }
  }, []);

  const handleIntercom = useCallback(async member => {
    showCallFeedback(member.id, 'intercom', 'sending');
    try {
      await backendApi.callTeamMember(member.id);
      showCallFeedback(member.id, 'intercom', 'sent');
    } catch (error) {
      showCallFeedback(member.id, 'intercom', 'error');
    }
  }, [showCallFeedback]);

  const handleVoiceCall = useCallback(async member => {
    showCallFeedback(member.id, 'voice', 'sending');
    try {
      await startCall(member);
      showCallFeedback(member.id, 'voice', 'sent');
    } catch (error) {
      showCallFeedback(member.id, 'voice', 'error');
    }
  }, [showCallFeedback, startCall]);

  useEffect(() => {
    if (providedData) return undefined;
    let mounted = true;

    const load = async () => {
      try {
        const freshData = await backendApi.getTeamTvDashboard();
        if (mounted) {
          if (previousTeamRef.current) {
            const previousById = new Map(previousTeamRef.current.map(member => [member.id, member]));
            freshData.team.forEach(member => {
              const previous = previousById.get(member.id);
              if (!previous) return;
              if (isTvMode && previous.status !== 'focus' && member.status === 'focus') {
                announcePomodoroStart({
                  user_id: member.id,
                  user_name: member.pomodoro_user_name || member.name,
                  duration_seconds: member.pomodoro_total_seconds,
                  started_at: member.pomodoro_started_at,
                  ends_at: member.status_ends_at,
                });
              }
              if (isTvMode && previous.status === 'focus' && member.status !== 'focus') {
                announcePomodoroEnd({
                  user_id: previous.id,
                  user_name: previous.pomodoro_user_name || previous.name,
                  started_at: previous.pomodoro_started_at,
                  ends_at: previous.status_ends_at,
                });
              }
            });
          }
          previousTeamRef.current = freshData.team;
          setData(freshData);
          setIsStale(false);
          setLoadError(false);
        }
      } catch (error) {
        if (mounted) {
          setIsStale(true);
          setLoadError(true);
        }
      }
    };

    load();
    window.addEventListener('pomodoro:changed', load);
    const timer = window.setInterval(load, 5000);
    return () => {
      mounted = false;
      window.removeEventListener('pomodoro:changed', load);
      window.clearInterval(timer);
    };
  }, [providedData, isTvMode]);

  useEffect(() => {
    if (providedData) return undefined;
    let mounted = true;

    const loadActivity = async () => {
      setActivityLoading(true);
      try {
        const freshActivity = await backendApi.getCollaboratorRings('sprint');
        if (mounted) setActivityData(freshActivity);
      } catch (error) {
        // L’écran continue d’afficher la dernière synthèse disponible.
      } finally {
        if (mounted) setActivityLoading(false);
      }
    };

    loadActivity();
    const timer = window.setInterval(loadActivity, 60000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [providedData]);

  useEffect(() => {
    if (providedData || !isTvMode) return undefined;
    let mounted = true;

    const loadIntercomCalls = async () => {
      try {
        const calls = await backendApi.getIntercomCalls('main');
        if (!mounted) return;
        calls.forEach(call => {
          if (knownIntercomIdsRef.current.has(call.id)) return;
          knownIntercomIdsRef.current.add(call.id);
          showIntercomCall(call);
        });
      } catch (error) {
        // Les autres données du dashboard restent disponibles si l'interphone est hors ligne.
      }
    };

    loadIntercomCalls();
    const timer = window.setInterval(loadIntercomCalls, 2000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [providedData, showIntercomCall, isTvMode]);

  useEffect(() => () => window.clearTimeout(intercomAlertTimerRef.current), []);

  useEffect(() => {
    if (providedData || !isTvMode) return undefined;
    let mounted = true;

    const loadAnnouncements = async () => {
      try {
        const announcements = await backendApi.getTeamTvAnnouncements();
        if (!mounted) return;

        if (!knownTicketIdsRef.current) {
          // Le premier chargement constitue la référence et ne lit pas les tickets historiques.
          knownTicketIdsRef.current = new Set(announcements.tickets.map(ticket => ticket.id));
        } else {
          announcements.tickets.forEach(ticket => {
            if (!knownTicketIdsRef.current.has(ticket.id)) announceNewTicket(ticket);
            // On conserve les identifiants déjà vus pour qu'une réponse Odoo vide ou
            // partielle ne transforme pas d'anciens tickets en fausses nouveautés.
            knownTicketIdsRef.current.add(ticket.id);
          });
        }

        const now = new Date();
        announcements.events.forEach(event => {
          const millisecondsUntilStart = new Date(event.starts_at).getTime() - now.getTime();
          if (millisecondsUntilStart > 0 && millisecondsUntilStart <= 10 * 60 * 1000) {
            announceUpcomingEvent(event, now);
          }
        });
      } catch (error) {
        // Le dashboard reste fonctionnel si le canal d'annonces est temporairement indisponible.
      }
    };

    loadAnnouncements();
    const timer = window.setInterval(loadAnnouncements, 10000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [providedData, isTvMode]);

  const displayData = useMemo(() => {
    if (!data || !activityData) return data;
    const activitiesByUser = new Map(activityData.collaborators.map(collaborator => [collaborator.id, collaborator]));
    const poActivitiesByUser = new Map((activityData.productOwners || []).map(productOwner => [productOwner.id, productOwner]));
    return {
      ...data,
      activity: activityData.summary,
      poSummary: activityData.poSummary,
      team: data.team.map(member => {
        const isProductOwner = isProductOwnerDepartment(member.department_name);
        return {
          ...member,
          activity: activitiesByUser.get(member.id) || null,
          poActivity: isProductOwner
            ? poActivitiesByUser.get(member.id)
              || member.poActivity
              || { testedUS: 0, doneUS: 0, donePct: null, validatedWithinTwoDaysUS: 0, validationWithinTwoDaysPct: null, sprintIntegrationUS: 0, backlogEligibleUS: 0, sprintIntegrationPct: null }
            : null,
        };
      }),
    };
  }, [data, activityData]);

  if (!displayData && loadError) return <main className="team-tv tv-loading" role="alert">Données Odoo indisponibles · nouvelle tentative automatique</main>;
  if (!displayData) return <main className="team-tv tv-loading" role="status" aria-label="Chargement des données Odoo" />;
  const unreadBySender = new Map(unreadMessages.map(message => [message.sender_id, message]));
  return (
    <>
      <TeamTvContent
        data={displayData}
        isStale={isStale}
        onIntercom={handleIntercom}
        onVoiceCall={handleVoiceCall}
        onChat={openChat}
        unreadBySender={unreadBySender}
        currentUserId={currentUserId}
        callFeedback={callFeedback}
        activeCall={activeCall}
        isTvMode={isTvMode}
        onTvModeChange={handleTvModeChange}
        activityPeriodMeta={activityData?.period || { period: 'sprint' }}
        activityLoading={activityLoading}
      />
      {chatMember && (
        <TeamChatPanel
          member={chatMember}
          currentUserId={currentUserId}
          onClose={() => setChatMember(null)}
          onConversationRead={clearUnreadForMember}
        />
      )}
    </>
  );
}

export { SAMPLE_DATA };
export default TeamTvDashboard;
